// 2026-06-13 pen 对齐 MaAa1:未订阅走 items-preview(不再裸撞 401)+ 介绍卡 + 文件列表卡
// (aBGfD → /s/:packId/files)+ 底部双态(已订阅=保存到网盘 / 未订阅=立即订阅)。
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Crown, Download, Share2 } from 'lucide-react'
import {
  GuardedButton,
  SocialBar,
  Spinner,
  SharePackRoute,
  apiClient,
  formatBytes,
  formatRelativeTime,
  useCurrentUser,
  copyToClipboard,
} from '@dogdisk/shared'
import type { SharePackMeta } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import { PackItemThumb } from '../components/pack-item-thumb'
import { fetchItemsPreview } from '../_lib/pack-api'
import { useMyVip, isVipUser } from '../_lib/vip-api'
import { useSocialStats, useToggleDislike, useToggleLike } from '../_lib/social-api'
import { usePackBasePath } from '../_lib/pack-route'

type Item = {
  id: string // resource_pack_item.id(save-to-pan 用)
  file_info_id: string
  name: string
  size_original: number | null
  mime_type: string
  position: number
  type?: string
  cover_thumb_url?: string | null
}

function PackContent({ pack }: { pack: SharePackMeta }) {
  const navigate = useNavigate()
  const base = usePackBasePath()
  const { status } = useCurrentUser()
  const [items, setItems] = useState<Item[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showVipModal, setShowVipModal] = useState(false)

  // D39/D40 SocialBar(资源包级)
  const vipQuery = useMyVip(status === 'authenticated')
  const isVip = isVipUser(vipQuery.data)
  const socialStats = useSocialStats('pack', pack.pack_id)
  const likeMut = useToggleLike('pack', pack.pack_id)
  const dislikeMut = useToggleDislike('pack', pack.pack_id)

  const frameTestId = 'MaAa1'

  // 未订阅数据路径:公开 items-preview(items 一旦就绪自动停)
  const previewQuery = useQuery({
    queryKey: ['pack', 'items-preview', pack.pack_id] as const,
    enabled: items === null,
    queryFn: () => fetchItemsPreview(pack.pack_id),
  })

  // 已登录进场静默探测(订阅者/owner GetItems 直接 200;未订阅 401 静默吞,由 preview 兜底)
  const probed = useRef(false)
  useEffect(() => {
    if (probed.current || status !== 'authenticated' || items) return
    probed.current = true
    void apiClient
      // 进场静默探测:未订阅返 401 TOKEN_INVALID 由 preview 兜底,
      // 只静默 TOKEN_INVALID(否则非订阅者每次进场都误弹「令牌无效」);5xx/网络错仍弹。
      .get<{ items: Item[] }>(`/gapi/public/share/${pack.pack_id}/items`, {
        silenceErrorCodes: ['TOKEN_INVALID'],
      })
      .then((res) => setItems(res.items))
      .catch(() => {})
  }, [status, items, pack.pack_id])

  const fetchItems = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get<{ items: Item[] }>(
        `/gapi/public/share/${pack.pack_id}/items`,
      )
      setItems(res.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }

  const subscribe = async () => {
    setLoading(true)
    setError(null)
    try {
      await apiClient.post(`/gapi/user/share/${pack.pack_id}/subscribe`, {})
      await fetchItems()
    } catch (e) {
      setError(e instanceof Error ? e.message : '订阅失败')
    } finally {
      setLoading(false)
    }
  }

  // 转存逻辑;session 校验由按钮处的 <GuardedButton requireAuth> 兜底,这里默认进来已登录。
  // 后端要求 item_ids 非空(resource_pack_item.id 列表),传全量 items。
  const saveToPan = async () => {
    if (!items || items.length === 0) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.post<{ folder_file_info_id?: string }>(
        `/gapi/user/share/${pack.pack_id}/save-to-pan`,
        { item_ids: items.map((it) => it.id) },
      )
      const folderId = res?.folder_file_info_id
      // 保存进以资源包名命名的文件夹 → 直接跳进该文件夹;缺字段兜底回文件页
      navigate(folderId ? `/folders/${folderId}` : '/files')
    } catch (e) {
      setError(e instanceof Error ? e.message : '转存失败')
    } finally {
      setLoading(false)
    }
  }

  const hasAccess = items !== null
  // 文件卡数据:有权限用 items,否则 preview
  const fileRows: Item[] =
    items ??
    (previewQuery.data ?? []).map((p) => ({
      id: p.item_id ?? p.name,
      file_info_id: p.item_id ?? p.name,
      name: p.name,
      size_original: p.size_original,
      mime_type: p.mime_type ?? '',
      position: p.position,
      type: p.type,
      cover_thumb_url: p.cover_thumb_url ?? null,
    }))

  return (
    <MobileScreen>
      <TopBar
        title="资源包订阅"
        right={
          <button
            type="button"
            data-testid="MaAa1-share-btn"
            aria-label="分享"
            onClick={() => void copyToClipboard(`${window.location.origin}/#/s/${pack.pack_id}`)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F6F7F8] text-[#1A1A1A]"
          >
            <Share2 size={18} aria-hidden="true" />
          </button>
        }
      />

      <main data-testid={frameTestId} className="flex flex-1 flex-col gap-6 px-6 pt-2 pb-40">
        {/* 介绍卡(mQLly IntroBox):名称 + 描述 + meta 行(文件数 · 总大小 · 更新时间) */}
        <section data-testid="MaAa1-intro" className="flex flex-col gap-3 rounded-xl bg-[#F6F7F8] p-4">
          <h2 className="text-[15px] font-semibold text-[#1A1A1A]">{pack.name}</h2>
          {pack.description && (
            <p className="text-[14px] leading-relaxed text-[#6B7280]">{pack.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#9CA3AF]">
            <span>{pack.item_count ?? 0} 个文件</span>
            {pack.total_size != null && <span>{formatBytes(pack.total_size)}</span>}
            {pack.updated_at && <span>更新于 {formatRelativeTime(pack.updated_at)}</span>}
          </div>
        </section>

        {/* 文件列表卡(V9KTu):查看全部在顶部 + 前 5 项 → /s/:packId/files */}
        <section className="flex flex-col gap-3 rounded-xl bg-[#F6F7F8] p-4">
          <h2 className="text-[15px] font-semibold text-[#1A1A1A]">文件列表</h2>
          <div className="flex flex-col gap-2 rounded-xl bg-white p-3">
            <button
              type="button"
              data-testid="aBGfD"
              onClick={() => navigate(`${base}/${pack.pack_id}/files`)}
              className="flex items-center justify-between px-1 pb-2 text-[13px] font-medium text-[#19caad]"
            >
              查看全部文件
              <ChevronRight size={16} aria-hidden="true" />
            </button>
            {fileRows.slice(0, 5).map((it) => (
              <div key={it.file_info_id} className="flex items-center gap-3">
                <PackItemThumb
                  coverThumbUrl={it.cover_thumb_url}
                  type={it.type ?? 'file'}
                  mimeType={it.mime_type}
                  name={it.name}
                  size={36}
                  iconSize={16}
                />
                <span className="flex-1 truncate text-[15px] font-medium text-[#1A1A1A]">{it.name}</span>
                <span className="text-[13px] text-[#9CA3AF]">{formatBytes(it.size_original ?? 0)}</span>
              </div>
            ))}
          </div>
        </section>

        {/* D39/D40 SocialBar — 资源包的喜欢 / 不喜欢(D4:转存收口到底部保存键) */}
        <section className="rounded-2xl bg-white">
          <SocialBar
            theme="light"
            isVip={isVip}
            stats={socialStats.data ?? null}
            isPending={likeMut.isPending || dislikeMut.isPending || loading}
            onLike={async () => { await likeMut.mutateAsync() }}
            onDislike={async () => { await dislikeMut.mutateAsync() }}
            onVipRequired={() => setShowVipModal(true)}
          />
        </section>

        {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}
      </main>

      {showVipModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
          onClick={() => setShowVipModal(false)}
        >
          <div
            className="w-full max-w-[320px] rounded-2xl bg-white p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-center text-[16px] font-semibold text-[#1A1A1A]">仅 VIP 可投票</p>
            <p className="mt-2 text-center text-[12px] text-[#9CA3AF]">升级 VIP 解锁喜欢 / 不喜欢功能</p>
            <button
              type="button"
              className="mt-4 h-11 w-full rounded-lg bg-[#19caad] text-[14px] font-semibold text-white"
              onClick={() => { setShowVipModal(false); navigate('/vip') }}
            >
              立即升级
            </button>
            <button
              type="button"
              className="mt-2 h-11 w-full rounded-lg bg-[#F6F7F8] text-[14px] font-medium text-[#1A1A1A]"
              onClick={() => setShowVipModal(false)}
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* 底部双态:已订阅(items 就绪)=保存到网盘;未订阅=立即订阅 */}
      <div
        className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-[402px] border-t border-[#F1F2F4] bg-white px-5 pt-3"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
      >
        {hasAccess ? (
          <GuardedButton
            requireAuth
            type="button"
            data-testid="MaAa1-save-btn"
            onClick={saveToPan}
            disabled={loading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#19caad] text-[18px] font-semibold text-white disabled:opacity-50"
          >
            <Download size={20} aria-hidden="true" />
            保存到我的网盘
          </GuardedButton>
        ) : pack.access_mode === 'free' ? (
          /* 存量 free 包兜底:后端 Subscribe 对非 paid 报 PackNotPaidMode,订阅按钮无意义 */
          <p
            data-testid="MaAa1-free-hint"
            className="flex h-12 w-full items-center justify-center text-[14px] text-[#9CA3AF]"
          >
            该分享暂不可订阅
          </p>
        ) : (
          <GuardedButton
            requireAuth
            type="button"
            data-testid="MaAa1-subscribe-btn"
            onClick={subscribe}
            disabled={loading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#1A1A1A] text-[16px] font-bold text-white disabled:opacity-50"
          >
            <Crown size={18} className="text-[#F59E0B]" aria-hidden="true" />
            {status === 'authenticated' ? '立即订阅' : '登录后订阅'}
            {pack.price_coin != null && (
              <span className="text-[14px] font-bold text-[#F59E0B]">· {pack.price_coin} 金币</span>
            )}
          </GuardedButton>
        )}
      </div>
    </MobileScreen>
  )
}

export function SharePackPage({ promo = false }: { promo?: boolean }) {
  return (
    <SharePackRoute
      setPromoCookies={promo}
      renderLoading={() => (
        <MobileScreen>
          <div className="flex flex-1 items-center justify-center"><Spinner className="text-gray-400" /></div>
        </MobileScreen>
      )}
      renderError={(err) => (
        <MobileScreen>
          <header className="flex items-center justify-center gap-2 px-6 pt-[44px] pb-3">
            <h1 className="text-[16px] font-semibold text-[#19caad]">绅士网盘</h1>
          </header>
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="text-[40px]">⚠️</span>
            <p className="text-[14px] text-[#FF6B6B]">{err instanceof Error ? err.message : '加载失败'}</p>
          </div>
        </MobileScreen>
      )}
      renderPage={(meta) => <PackContent pack={meta} />}
    />
  )
}
